import { useI18n } from 'vue-i18n';
import moment from 'moment-timezone';
import { isBeforeToday } from '@/helpers/dateUtiles';

export const TIPOS_ORDEN = {
  NOMBRE: 'nombre',
  PRECIO_ASC: 'precio-asc',
  PRECIO_DESC: 'precio-desc',
  RECOMENDADO: 'recomendado',
};

export const TIPOS_FILTROS = {
  OPCIONES: 'opciones',
  RANGO: 'rango',
  MES: 'mes',
  UNICO: 'unico',
};

export const IDS_FILTROS = {
  UBICACIONES: 'ubicaciones',
  TIPO_ALOJAMIENTO: 'tipo-alojamiento',
  NUMERO_PLAZAS: 'numero-plazas',
  PRECIO: 'precio',
  LUGAR_EXPERIENCIA: 'lugar-experiencia',
  MESES: 'meses',
  CATEGORIAS: 'categorias',
  ESTADO_RESERVA: 'estado-reserva',
};

export const ESTADO_RESERVA_VALORES = {
  PROXIMAS: 'proximas',
  PASADAS: 'pasadas',
};

/////////////////////
// ALOJAMIENTOS
/////////////////////

export function getOrdenAlojamientos() {
  const { t } = useI18n();
  return [
    { id: TIPOS_ORDEN.NOMBRE, nombre: t('general.nombre') },
    { id: TIPOS_ORDEN.PRECIO_ASC, nombre: t('general.precioDeMenorAMayor') },
    { id: TIPOS_ORDEN.PRECIO_DESC, nombre: t('general.precioDeMayorAMenor') },
  ];
}

export function generateFiltrosAlojamientos(alojamientos = []) {
  if (alojamientos.length === 0) {
    return [];
  }

  const { t } = useI18n();
  let filtros = [];

  // UBICACIONES
  const ubicacionesUnicas = new Set();
  alojamientos.forEach(alojamiento => {
    if (alojamiento.ubicacion) {
      ubicacionesUnicas.add(alojamiento.ubicacion);
    }
  });

  if (ubicacionesUnicas.size > 0) {
    filtros.push({
      id: IDS_FILTROS.UBICACIONES,
      titulo: t('alojamiento.filtros.titulo.ubicaciones'),
      tipo: TIPOS_FILTROS.OPCIONES,
      opciones: Array.from(ubicacionesUnicas).map(ubicacion => ({
        id: `ubicacion-${ubicacion}`,
        nombre: ubicacion,
      })),
    });
  }

  // TIPO DE CUPO
  const tiposCupoUnicos = new Set();
  alojamientos.forEach(alojamiento => {
    if (alojamiento.hasHabitaciones) {
      alojamiento.cupos.forEach(cupo => {
        if (cupo.tipo && cupo.tipo.nombre) {
          tiposCupoUnicos.add(cupo.tipo.nombre);
        }
      });
    }
  });
  if (tiposCupoUnicos.size > 0) {
    filtros.push({
      id: IDS_FILTROS.TIPO_ALOJAMIENTO,
      titulo: t('alojamiento.filtros.titulo.tipoAlojamiento'),
      tipo: TIPOS_FILTROS.OPCIONES,
      opciones: Array.from(tiposCupoUnicos).map(tipoCupo => ({
        id: `tipo-${tipoCupo}`,
        nombre: tipoCupo,
      })),
    });
  }

  // NUMERO DE PLAZAS
  const plazasUnicas = new Set();
  alojamientos.forEach(alojamiento => {
    if (alojamiento.hasHabitaciones) {
      alojamiento.cupos.forEach(cupo => {
        if (cupo.plazas) {
          plazasUnicas.add(cupo.plazas);
        }
      });
    }
  });

  if (plazasUnicas.size > 0) {
    filtros.push({
      id: IDS_FILTROS.NUMERO_PLAZAS,
      titulo: t('alojamiento.filtros.titulo.numPlazas'),
      tipo: TIPOS_FILTROS.OPCIONES,
      opciones: Array.from(plazasUnicas).map(plazas => ({
        id: `plazas-${plazas}`,
        nombre: `${plazas} ${t('general.plaza')}`,
        valor: plazas,
      })),
    });
  }

  // PRECIO
  const precios = [];
  alojamientos.forEach(alojamiento => {
    alojamiento.cupos.forEach(cupo => {
      if (cupo.precioDesde) {
        precios.push(cupo.precioDesde);
      }
    });
  });

  if (precios.length > 0) {
    filtros.push({
      id: IDS_FILTROS.PRECIO,
      titulo: t('alojamiento.filtros.titulo.precio'),
      tipo: TIPOS_FILTROS.RANGO,
      min: Math.min(...precios),
      max: Math.max(...precios),
    });
  }

  return filtros;
}

/////////////////////
// EXPERIENCIAS/VIAJES
/////////////////////

export function getOrdenExperiencias() {
  const { t } = useI18n();
  return [
    { id: TIPOS_ORDEN.RECOMENDADO, nombre: t('general.recomendado') },
    { id: TIPOS_ORDEN.NOMBRE, nombre: t('general.nombre') },
    { id: TIPOS_ORDEN.PRECIO_ASC, nombre: t('general.precioDeMenorAMayor') },
    { id: TIPOS_ORDEN.PRECIO_DESC, nombre: t('general.precioDeMayorAMenor') },
  ];
}

export function generateFiltrosExperiencias(experiencias = []) {
  if (experiencias.length === 0) {
    return [];
  }

  const { t } = useI18n();
  let filtros = [];

  // LUGAR EXPERIENCIA
  const lugaresExperienciaUnicos = new Map();
  experiencias.forEach(experiencia => {
    if (experiencia.lugarExperiencia && experiencia.lugarExperiencia.id) {
      const lugarId = experiencia.lugarExperiencia.id;
      if (!lugaresExperienciaUnicos.has(lugarId)) {
        lugaresExperienciaUnicos.set(lugarId, experiencia.lugarExperiencia);
      }
    }
  });

  if (lugaresExperienciaUnicos.size > 0) {
    filtros.push({
      id: IDS_FILTROS.LUGAR_EXPERIENCIA,
      titulo: t('viajesCatalogo.filtros.lugarExperiencia'),
      tipo: TIPOS_FILTROS.OPCIONES,
      opciones: Array.from(lugaresExperienciaUnicos.values()).map(lugarExperiencia => ({
        id: `lugar-experiencia-${lugarExperiencia.id}`,
        nombre: lugarExperiencia.nombre,
        valor: lugarExperiencia,
      })),
    });
  }

  // MESES
  const yearsUnicos = new Set();
  experiencias.forEach(experiencia => {
    if (experiencia.fechaInicio) {
      const fechaInicio = moment(experiencia.fechaInicio);
      yearsUnicos.add(fechaInicio.year());
    }
  });

  if (yearsUnicos.size > 0) {
    const yearsDisponibles = Array.from(yearsUnicos).sort((a, b) => a - b);
    filtros.push({
      id: IDS_FILTROS.MESES,
      titulo: t('viajesCatalogo.filtros.meses'),
      tipo: TIPOS_FILTROS.MES,
      yearsDisponibles,
    });
  }

  // TIPOS
  const tiposViajeUnicos = new Map();
  experiencias.forEach(experiencia => {
    if (experiencia.tipos?.length) {
      experiencia.tipos.forEach(tipo => {
        if (tipo?.id && !tiposViajeUnicos.has(tipo.id)) {
          tiposViajeUnicos.set(tipo.id, tipo);
        }
      });
    }
  });

  if (tiposViajeUnicos.size > 0) {
    filtros.push({
      id: IDS_FILTROS.CATEGORIAS,
      titulo: t('viajesCatalogo.filtros.categorias'),
      tipo: TIPOS_FILTROS.OPCIONES,
      opciones: Array.from(tiposViajeUnicos.values()).map(tipo => ({
        id: `categoria-${tipo.id}`,
        nombre: tipo.descripcion || tipo.nombre || tipo.id,
        valor: tipo,
      })),
    });
  }

  // PRECIO
  const precios = experiencias.map(experiencia => experiencia.precioDesde).filter(precio => precio && precio > 0);

  if (precios.length > 0) {
    filtros.push({
      id: IDS_FILTROS.PRECIO,
      titulo: t('viajesCatalogo.filtros.precios'),
      tipo: TIPOS_FILTROS.RANGO,
      min: Math.min(...precios),
      max: Math.max(...precios),
    });
  }

  return filtros;
}

/////////////////////
// RESERVAS (MIS RESERVAS)
/////////////////////

export function generateFiltrosReservas() {
  const { t } = useI18n();

  return [
    {
      id: IDS_FILTROS.ESTADO_RESERVA,
      titulo: t('reserva.filtros.titulo'),
      tipo: TIPOS_FILTROS.UNICO,
      opciones: [
        {
          id: 'estado-proximas',
          nombre: t('reserva.filtros.proximas'),
          valor: ESTADO_RESERVA_VALORES.PROXIMAS,
        },
        {
          id: 'estado-pasadas',
          nombre: t('reserva.filtros.pasadas'),
          valor: ESTADO_RESERVA_VALORES.PASADAS,
        },
      ],
    },
  ];
}

export function filterGrupos(grupos, filtrosAplicados) {
  if (!filtrosAplicados || filtrosAplicados.length === 0) {
    return grupos;
  }

  const filtrosPorTipo = groupFiltrosPorTipo(filtrosAplicados);
  const filtrosEstado = filtrosPorTipo[IDS_FILTROS.ESTADO_RESERVA];

  if (!filtrosEstado?.length) {
    return grupos;
  }

  const estadoFiltro = filtrosEstado[filtrosEstado.length - 1];
  const estadoValor = estadoFiltro.valor?.valor ?? estadoFiltro.valor;

  return grupos.filter(grupo => {
    const fechaFin = grupo.viaje?.fechaFin;
    if (!fechaFin) {
      return false;
    }

    if (estadoValor === ESTADO_RESERVA_VALORES.PROXIMAS) {
      return !isBeforeToday(fechaFin);
    }

    if (estadoValor === ESTADO_RESERVA_VALORES.PASADAS) {
      return isBeforeToday(fechaFin);
    }

    return true;
  });
}

/////////////////////
// FILTRAR Y ORDENAR (EXPERIENCIAS)
/////////////////////

export function groupFiltrosPorTipo(filtrosAplicados) {
  return filtrosAplicados.reduce((acc, filtro) => {
    if (!acc[filtro.idFiltro]) {
      acc[filtro.idFiltro] = [];
    }
    acc[filtro.idFiltro].push(filtro);
    return acc;
  }, {});
}

export function filterExperiencias(experiencias, filtrosAplicados) {
  if (!filtrosAplicados || filtrosAplicados.length === 0) {
    return experiencias;
  }

  const filtrosPorTipo = groupFiltrosPorTipo(filtrosAplicados);

  return experiencias.filter(experiencia => {
    const filtrosLugar = filtrosPorTipo[IDS_FILTROS.LUGAR_EXPERIENCIA];
    if (filtrosLugar?.length > 0) {
      if (!experiencia.lugarExperiencia) {
        return false;
      }
      const matchLugar = filtrosLugar.some(filtroLugar => {
        const lugarValor = filtroLugar.valor?.valor || filtroLugar.valor;
        return experiencia.lugarExperiencia.id && lugarValor?.id && experiencia.lugarExperiencia.id === lugarValor.id;
      });
      if (!matchLugar) {
        return false;
      }
    }

    const filtrosMeses = filtrosPorTipo[IDS_FILTROS.MESES];
    if (filtrosMeses?.length > 0 && experiencia.fechaInicio && experiencia.fechaFin) {
      const fechaInicio = moment(experiencia.fechaInicio);
      const fechaFin = moment(experiencia.fechaFin);
      const matchMes = filtrosMeses.some(filtroMes => {
        const selectedYear = filtroMes.valor.year;
        const selectedMonthNumber = filtroMes.valor.month;
        const selectedFecha = moment({ year: selectedYear, month: selectedMonthNumber, date: 1 });
        const selectedFechaFin = moment(selectedFecha).endOf('month');

        return !selectedFecha.isAfter(fechaFin, 'day') && !selectedFechaFin.isBefore(fechaInicio, 'day');
      });
      if (!matchMes) {
        return false;
      }
    }

    const filtrosPrecio = filtrosPorTipo[IDS_FILTROS.PRECIO];
    if (filtrosPrecio?.length > 0) {
      const precioFiltro = filtrosPrecio[0]?.valor;
      if (
        !experiencia.precioDesde ||
        experiencia.precioDesde < precioFiltro.min ||
        experiencia.precioDesde > precioFiltro.max
      ) {
        return false;
      }
    }

    const filtrosTipos = filtrosPorTipo[IDS_FILTROS.CATEGORIAS];
    if (filtrosTipos?.length > 0) {
      if (!experiencia.tipos || experiencia.tipos.length === 0) {
        return false;
      }
      const matchTipo = filtrosTipos.some(filtroTipo => {
        const tipoValor = filtroTipo.valor?.valor || filtroTipo.valor;
        return experiencia.tipos.some(tipo => {
          if (tipo.id && tipoValor?.id) {
            return tipo.id === tipoValor.id;
          }
          return false;
        });
      });
      if (!matchTipo) {
        return false;
      }
    }

    return true;
  });
}

export function orderExperiencias(experiencias, orden) {
  if (!orden?.id) return experiencias;

  const experienciasOrdenadas = [...experiencias];

  switch (orden.id) {
    case TIPOS_ORDEN.NOMBRE:
      experienciasOrdenadas.sort((a, b) => a.nombre.localeCompare(b.nombre));
      break;

    case TIPOS_ORDEN.PRECIO_ASC:
      experienciasOrdenadas.sort((a, b) => {
        const precioA = a.precioDesde;
        const precioB = b.precioDesde;
        return precioA - precioB;
      });
      break;

    case TIPOS_ORDEN.PRECIO_DESC:
      experienciasOrdenadas.sort((a, b) => {
        const precioA = a.precioDesde;
        const precioB = b.precioDesde;
        return precioB - precioA;
      });
      break;
  }

  return experienciasOrdenadas;
}

/////////////////////
// AGRUPACIONES
/////////////////////

export function getOrdenAgrupaciones() {
  const { t } = useI18n();
  return [
    { id: TIPOS_ORDEN.RECOMENDADO, nombre: t('general.recomendado') },
    { id: TIPOS_ORDEN.NOMBRE, nombre: t('general.nombre') },
  ];
}

export function generateFiltrosAgrupaciones(agrupaciones = []) {
  if (agrupaciones.length === 0) {
    return [];
  }

  const { t } = useI18n();
  let filtros = [];

  // UBICACIONES
  const ubicacionesUnicas = new Set();
  agrupaciones.forEach(agrupacion => {
    if (agrupacion.ubicacion) {
      ubicacionesUnicas.add(agrupacion.ubicacion);
    }
  });

  if (ubicacionesUnicas.size > 0) {
    filtros.push({
      id: IDS_FILTROS.UBICACIONES,
      titulo: t('alojamiento.filtros.titulo.ubicaciones'),
      tipo: TIPOS_FILTROS.OPCIONES,
      opciones: Array.from(ubicacionesUnicas).map(ubicacion => ({
        id: `ubicacion-${ubicacion}`,
        nombre: ubicacion,
      })),
    });
  }

  // MESES
  if (agrupaciones.some(agrupacion => agrupacion.fechaInicio && agrupacion.fechaFin)) {
    const currentYear = new Date().getFullYear();
    const yearsDisponibles = [];
    for (let i = currentYear; i <= currentYear + 10; i++) {
      yearsDisponibles.push(i);
    }

    filtros.push({
      id: IDS_FILTROS.MESES,
      titulo: t('viajesCatalogo.filtros.meses'),
      tipo: TIPOS_FILTROS.MES,
      yearsDisponibles,
    });
  }

  return filtros;
}

export function filterAgrupaciones(agrupaciones, filtrosAplicados) {
  if (!filtrosAplicados || filtrosAplicados.length === 0) {
    return agrupaciones;
  }

  const filtrosPorTipo = groupFiltrosPorTipo(filtrosAplicados);

  return agrupaciones.filter(agrupacion => {
    const filtrosUbicacion = filtrosPorTipo[IDS_FILTROS.UBICACIONES];
    if (filtrosUbicacion?.length > 0) {
      if (!agrupacion.ubicacion) {
        return false;
      }
      const matchUbicacion = filtrosUbicacion.some(filtroUbicacion => {
        const ubicacionNombre = filtroUbicacion.valor?.nombre || filtroUbicacion.valor;
        return agrupacion.ubicacion === ubicacionNombre;
      });
      if (!matchUbicacion) {
        return false;
      }
    }

    const filtrosMeses = filtrosPorTipo[IDS_FILTROS.MESES];
    if (filtrosMeses?.length > 0 && agrupacion.fechaInicio && agrupacion.fechaFin) {
      const fechaInicio = moment(agrupacion.fechaInicio);
      const fechaFin = moment(agrupacion.fechaFin);
      const matchMes = filtrosMeses.some(filtroMes => {
        const selectedYear = filtroMes.valor.year;
        const selectedMonthNumber = filtroMes.valor.month;
        const selectedFecha = moment({ year: selectedYear, month: selectedMonthNumber, date: 1 });
        const selectedFechaFin = moment(selectedFecha).endOf('month');

        return !selectedFecha.isAfter(fechaFin, 'day') && !selectedFechaFin.isBefore(fechaInicio, 'day');
      });
      if (!matchMes) {
        return false;
      }
    }

    return true;
  });
}
